import PropTypes from 'prop-types';
import { useMemo } from 'react';

import useMediaQuery from '@mui/material/useMediaQuery';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import { useColorScheme } from '@mui/material/styles';

// project imports
import DrawerHeader from './DrawerHeader';
import DrawerContent from './DrawerContent';
import MiniDrawerStyled from './MiniDrawerStyled';

import { DRAWER_WIDTH } from 'config';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import useConfig from 'hooks/useConfig';

// ==============================|| MAIN LAYOUT - DRAWER ||============================== //

export default function MainDrawer({ window }) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  const { state } = useConfig();
  const { mode } = useColorScheme();

  // responsive drawer container
  const container = window !== undefined ? () => window().document.body : undefined;

  // header content
  const drawerContent = useMemo(() => <DrawerContent />, []);
  const drawerHeader = useMemo(() => <DrawerHeader open={drawerOpen} />, [drawerOpen]);

  const activeMode = mode || 'dark';
  const isGlass = activeMode === 'dark' && state.darkModeStyle === 'discord-glass';

  const glassStyle = isGlass ? {
    bgcolor: 'background.sidebar',
    backdropFilter: 'blur(20px) saturate(130%)',
    borderRight: '1px solid rgba(255, 255, 255, 0.08) !important',
    backgroundImage: 'none !important'
  } : {
    bgcolor: 'background.sidebar',
    backgroundImage: 'none !important'
  };

  return (
    <Box component="nav" sx={{ flexShrink: { md: 0 }, zIndex: 1200 }} aria-label="mailbox folders">
      {!downLG ? (
        <MiniDrawerStyled 
          variant="permanent" 
          open={drawerOpen} 
          slotProps={{ 
            paper: { 
              'data-color-scheme': activeMode,
              sx: {
                ...glassStyle,
                borderRight: '1px solid',
                borderRightColor: 'divider'
              }
            } 
          }}
        >
          {drawerHeader}
          {drawerContent}
        </MiniDrawerStyled>
      ) : (
        <Drawer
          container={container}
          variant="temporary"
          open={drawerOpen}
          onClose={() => handlerDrawerOpen(!drawerOpen)}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: drawerOpen ? 'block' : 'none', lg: 'none' } }}
          slotProps={{
            paper: {
              'data-color-scheme': activeMode,
              sx: {
                boxSizing: 'border-box',
                width: { xs: 'min(86vw, 260px)', sm: DRAWER_WIDTH },
                boxShadow: 'none !important',
                borderRight: '1px solid',
                borderRightColor: 'divider',
                ...glassStyle
              }
            }
          }}
        >
          {drawerHeader}
          {drawerContent}
        </Drawer>
      )}
    </Box>
  );
}

MainDrawer.propTypes = { window: PropTypes.func };
